// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Key derivation, message encryption, and the Short Authentication String.
 * Everything derives from the room passphrase, which never leaves the device.
 *
 * Transport is already private without this: Chatwell is a direct WebRTC mesh
 * (DTLS on the wire), and trystero encrypts the signalling handshake with the
 * passphrase so a relay can't read the SDP or swap DTLS fingerprints. On top of
 * that we wrap every message's bytes in AES-GCM-256 keyed from the passphrase —
 * defence in depth that binds the payload to the secret the room shares,
 * independent of the transport. The SAS below lets people confirm out loud that
 * they are all in the same room, catching a relay that tries to bridge two.
 *
 * Honesty note: this is one static key derived from the passphrase — there is no
 * forward-secrecy ratchet in v1. The Threat Model modal states this plainly.
 */
import { WORDS } from './wordlist.ts';

const enc = new TextEncoder();

async function sha256(...parts: Uint8Array[]): Promise<Uint8Array> {
  const total = parts.reduce((n, p) => n + p.byteLength, 0);
  const buf = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    buf.set(p, off);
    off += p.byteLength;
  }
  return new Uint8Array(await crypto.subtle.digest('SHA-256', buf as BufferSource));
}

function toHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

/**
 * Derive the 32-byte room secret from the passphrase, salted by the room id, via
 * PBKDF2-SHA-256. This is the root of the SAS, the fingerprint, and the AES key.
 */
export async function deriveRoomSecret(pass: string, room: string): Promise<Uint8Array> {
  const base = await crypto.subtle.importKey('raw', enc.encode(pass) as BufferSource, 'PBKDF2', false, ['deriveBits']);
  const salt = enc.encode(`chatwell:v1:${room}`) as BufferSource;
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt, iterations: 150_000, hash: 'SHA-256' },
    base,
    256,
  );
  return new Uint8Array(bits);
}

/** Import the passphrase-derived secret as an AES-GCM key for message encryption. */
export async function deriveMessageKey(pass: string, room: string): Promise<CryptoKey> {
  const secret = await deriveRoomSecret(pass, room);
  return crypto.subtle.importKey('raw', secret as BufferSource, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

/** Encrypt bytes with a fresh random 12-byte IV. Returns iv + ciphertext. */
export async function encryptBytes(key: CryptoKey, plain: Uint8Array): Promise<{ iv: Uint8Array; cipher: Uint8Array }> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const buf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, plain as BufferSource);
  return { iv, cipher: new Uint8Array(buf) };
}

/** Decrypt ciphertext produced by {@link encryptBytes}. Throws on a bad key/tag. */
export async function decryptBytes(key: CryptoKey, iv: Uint8Array, cipher: Uint8Array): Promise<Uint8Array> {
  const buf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, cipher as BufferSource);
  return new Uint8Array(buf);
}

/**
 * Four SAS words, derived deterministically from (pass, room). Everyone in the
 * room derives the SAME words from the shared secret, so reading them aloud
 * confirms all sides really share the passphrase — a relay bridging two
 * different rooms would make the words disagree.
 */
export async function deriveSasWords(pass: string, room: string): Promise<string[]> {
  const secret = await deriveRoomSecret(pass, room);
  const digest = await sha256(secret, enc.encode('sas'));
  const out: string[] = [];
  for (let i = 0; i < 4; i++) out.push(WORDS[digest[i] % WORDS.length]);
  return out;
}

/** Hex fingerprint of the room secret — a compact, comparable trust anchor. */
export async function deriveFingerprint(pass: string, room: string): Promise<string> {
  const secret = await deriveRoomSecret(pass, room);
  const digest = await sha256(secret, enc.encode('fingerprint'));
  return toHex(digest.subarray(0, 8));
}
